import { useEffect } from 'react';
import { useLocation, Navigate, Outlet, PathRouteProps } from 'react-router-dom';

import { useAuth } from '@global-contexts/auth';
import { useGTMContext } from '@global-contexts/gtm';
import { useVersioning } from '@global-contexts/versioning';
import { isJWTExpired } from '@global-contexts/utils/authUtils';
import { sessionSnapshot, renewSession, isSessionRenewing, isSessionRecovering } from '@global-utils/sessionRenewal';

import FullScreenLoading from '@global-components/FullScreenLoading';
import AuthenticatedLayout from '@global-layouts/Authenticated';

type Role = 'admin' | 'manager';

interface PrivateRouteProps extends PathRouteProps {
  roles: Role[];
}

export const hasPrivateRouteAccess = (user: { permissions?: string[] } | null, roles: Role[]) =>
  !roles.length || roles.some((role) => Boolean(user?.permissions?.includes(role)));

const PrivateRoute = ({ roles = [], ...rest }: PrivateRouteProps) => {
  const location = useLocation();
  const { started, loading: isLoadingAuth, authTokenRefreshChecking, user, isAuthenticated, company } = useAuth();
  const { isNewReview } = useVersioning();
  const { sendDataLayer } = useGTMContext();
  const redirectTo = `${location.pathname}${location.search}`;
  const toLogin = location.pathname !== '/' ? `/login?redirectTo=${encodeURIComponent(redirectTo)}` : '/login';
  const { access, refresh, generation, impersonation } = sessionSnapshot();
  const renewableAccessExpired = Boolean(
    access && refresh && !impersonation && isJWTExpired(access).accessTokenIsExpired
  );
  // Retain the mounted route until the effect starts renewal. This is UI lifetime,
  // not authorization: the shared request gate still blocks expired credentials.
  const retainedSession = isAuthenticated() || renewableAccessExpired;

  useEffect(() => {
    const current = sessionSnapshot();
    if (
      renewableAccessExpired &&
      current.access === access &&
      current.refresh === refresh &&
      current.generation === generation &&
      current.impersonation === impersonation &&
      !isSessionRenewing() &&
      !isSessionRecovering()
    ) {
      void renewSession();
    }
  }, [renewableAccessExpired, access, refresh, generation, impersonation]);

  useEffect(() => {
    document.title = 'BuilderPad';
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: safe to ignore
  useEffect(() => {
    if (user && company.name && company.id) {
      sendDataLayer({
        event: 'page-load',
        page_url: location.pathname
      });
    }
  }, [location, user, company]);

  // Check if the JWT token needs to be refreshed by Auth Context. If it does, wait for it to finish. It checks only once on the first render/load
  if (!authTokenRefreshChecking) {
    return FullScreenLoading();
  }

  if (!retainedSession && !started && !isLoadingAuth) {
    return <Navigate to={toLogin} />;
  }

  if (started && !retainedSession) {
    return <Navigate to={toLogin} />;
  }

  if (retainedSession && !started && !isLoadingAuth) {
    return FullScreenLoading();
  }
  if (!started && isLoadingAuth) {
    return FullScreenLoading();
  }

  if (!hasPrivateRouteAccess(user, roles)) {
    return <Navigate to="/" />;
  }

  if (['/inbox/new-invoice/:id', '/inbox/new-review/:id'].includes(rest.path!) && !isNewReview) {
    return <Navigate to="/" />;
  }

  if (user) {
    return (
      <AuthenticatedLayout>
        <Outlet />
      </AuthenticatedLayout>
    );
  }

  return <Navigate to="/login" />;
};

export default PrivateRoute;
